import { Search, X } from 'lucide-react'
import { CustomSelect } from '~/components/ui/custom-select'
import { DatePicker } from '~/components/ui/date-picker'
import type { IngestBatch, MonitoringIndicator } from '~/api/types'
import { formatDate } from '~/lib/date-utils'

export interface ReadingsFilterBarProps {
    fieldId: string
    setFieldId(value: string): void
    indicators: MonitoringIndicator[]
    q: string
    setQ(value: string): void
    dateFrom: string
    setDateFrom(value: string): void
    dateTo: string
    setDateTo(value: string): void
    batchId: string
    setBatchId(value: string): void
    batches: IngestBatch[]
    hasFilter: boolean
    resetFilters(): void
}

/** 필터 — 지표 선택 · 값 검색 · 날짜 범위 · 적재 배치 */
export function ReadingsFilterBar({
    fieldId,
    setFieldId,
    indicators,
    q,
    setQ,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    batchId,
    setBatchId,
    batches,
    hasFilter,
    resetFilters,
}: ReadingsFilterBarProps) {
    return (
        <div className='border-border mb-3 flex flex-wrap items-end gap-2 rounded-md border border-dashed p-3'>
            <label className='grid gap-1'>
                <span className='text-muted-foreground text-xs'>지표</span>
                <CustomSelect
                    ariaLabel='지표 필터'
                    value={fieldId}
                    onValueChange={(v) => setFieldId(v)}
                    containerClassName='w-56'
                    className='border-border h-9'
                    searchable
                    searchPlaceholder='지표 검색…'
                    options={[
                        { value: '', label: '전체 지표' },
                        ...indicators.map((i) => ({
                            value: i.fieldId,
                            label: `${i.nameKo}${i.unit ? ` (${i.unit})` : ''}`,
                        })),
                    ]}
                />
            </label>
            <label className='grid gap-1'>
                <span className='text-muted-foreground text-xs'>값 검색</span>
                <span className='border-border bg-card flex h-9 w-40 items-center gap-1.5 rounded-md border px-2.5'>
                    <Search className='text-muted-foreground size-3.5 shrink-0' />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder='예: 4.5'
                        className='text-foreground w-full bg-transparent text-sm outline-none'
                    />
                </span>
            </label>
            <label className='grid gap-1'>
                <span className='text-muted-foreground text-xs'>시작일</span>
                <DatePicker
                    ariaLabel='시작일'
                    value={dateFrom}
                    onChange={(v) => setDateFrom(v)}
                    containerClassName='w-40'
                    className='h-9'
                />
            </label>
            <label className='grid gap-1'>
                <span className='text-muted-foreground text-xs'>종료일</span>
                <DatePicker
                    ariaLabel='종료일'
                    value={dateTo}
                    onChange={(v) => setDateTo(v)}
                    containerClassName='w-40'
                    className='h-9'
                />
            </label>
            {batches.length > 0 ? (
                <label className='grid gap-1'>
                    <span className='text-muted-foreground text-xs'>
                        적재 배치
                    </span>
                    <CustomSelect
                        ariaLabel='적재 배치 필터'
                        value={batchId}
                        onValueChange={(v) => setBatchId(v)}
                        containerClassName='w-64'
                        className='border-border h-9'
                        searchable
                        searchPlaceholder='배치 검색…'
                        options={[
                            { value: '', label: '전체 배치' },
                            ...batches.map((b) => ({
                                value: b.id,
                                label: `${
                                    b.filename ??
                                    `${b.kind === 'image' ? '이미지' : 'CSV'} ${b.rowCount}행`
                                } · ${formatDate(b.createdAt)}`,
                            })),
                        ]}
                    />
                </label>
            ) : null}
            {hasFilter ? (
                <button
                    type='button'
                    onClick={resetFilters}
                    className='text-muted-foreground hover:text-foreground flex h-9 items-center gap-1 text-xs'>
                    <X className='size-3.5' />
                    필터 초기화
                </button>
            ) : null}
        </div>
    )
}
